import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { ACCENT, formatMXN } from "../cuentas/shared";
import { getMonthRange, formatCalendarDate, isInRange, INSTANTE } from "../../utils/dates";
import { getValorEvento } from "../../utils/valorEvento";
import { costosPorEvento } from "../../utils/costos";
import { asientosDeDeposito } from "../../utils/ledger";
import { cancelacionesRetenidas } from "../../utils/reportes";
import { useSerieAnual, SelectorAnio } from "./shared";

// Estado de resultados DEVENGADO: el año mes por mes leído por los eventos
// que ocurren en cada mes — valor, costos del evento y utilidad, se cobren
// cuando se cobren. El dinero que entra y sale cada mes es otro reporte
// (Flujo de caja): son preguntas distintas y viven separadas a propósito.
// Los meses por venir muestran solo el valor contratado; su utilidad se
// conoce cuando ocurren.
//
// Los depósitos aplicados (el venue se quedó la garantía) son ingreso del
// mes en que se aplicaron, pero NO son ingreso de ningún evento: van en su
// columna, y el Resultado del mes los suma a la utilidad de eventos. Las dos
// columnas solo aparecen si el año tiene alguno — sin ellos, Resultado y
// Utilidad serían la misma cifra dos veces.
//
// Las cancelaciones siguen la misma lógica: lo que el venue retuvo de un
// evento cancelado (menos lo que ese evento sí gastó) no es utilidad de un
// evento que no ocurrió, pero sí resultado del venue en el mes en que se
// canceló. Columna propia, solo si el año tiene alguna.
const ReporteEstadoResultados = ({ depositos: depositosProp = null }) => {
  const navigate = useNavigate();
  const { anio, years, setAnio, serie, tz, events, costos, refunds, depositos } = useSerieAnual(depositosProp);
  const extras = (m) => (m.depositosAplicados || 0) + (m.cancelaciones || 0);

  // Resultado y cartera no se suman en el mismo renglón: uno es lo que ya
  // pasó, el otro lo que está contratado.
  const transcurrido = useMemo(
    () =>
      serie
        .filter((m) => !m.futuro)
        .reduce(
          (acc, m) => ({
            eventos: acc.eventos + m.eventos,
            valor: acc.valor + m.valor,
            costosEventos: acc.costosEventos + m.costosEventos,
            utilidad: acc.utilidad + m.utilidad,
            depositosAplicados: acc.depositosAplicados + (m.depositosAplicados || 0),
            cancelaciones: acc.cancelaciones + (m.cancelaciones || 0),
          }),
          { eventos: 0, valor: 0, costosEventos: 0, utilidad: 0, depositosAplicados: 0, cancelaciones: 0 }
        ),
    [serie]
  );
  const hayAplicados = useMemo(() => serie.some((m) => (m.depositosAplicados || 0) > 0), [serie]);
  const hayCancelaciones = useMemo(() => serie.some((m) => Math.abs(m.cancelaciones || 0) > 0.01), [serie]);
  // Con algún ingreso fuera de eventos, Utilidad y Resultado dejan de ser la
  // misma cifra y el Resultado aparece.
  const hayResultado = hayAplicados || hayCancelaciones;
  const columnas = 5 + (hayAplicados ? 1 : 0) + (hayCancelaciones ? 1 : 0) + (hayResultado ? 1 : 0);
  const porVenir = useMemo(
    () =>
      serie
        .filter((m) => m.futuro)
        .reduce(
          (acc, m) => ({ eventos: acc.eventos + m.eventos, valor: acc.valor + m.valor }),
          { eventos: 0, valor: 0 }
        ),
    [serie]
  );

  // Drill-down: los eventos del mes, con su valor y su utilidad.
  const [mesAbierto, setMesAbierto] = useState(null);
  const detalleEventos = useMemo(() => {
    if (!mesAbierto) return null;
    const { from, to } = getMonthRange(mesAbierto);
    const porEvento = costosPorEvento(costos);
    const t = (v) => (v?.toDate ? v.toDate() : new Date(v || 0));
    return (events || [])
      .filter(
        (e) =>
          e?.eventType === "event" &&
          e.status !== "cancelado" &&
          isInRange(e.startTime, from, to, tz, INSTANTE)
      )
      .map((e) => {
        const valor = getValorEvento(e);
        const costosEv = porEvento[e.id]?.total || 0;
        return {
          id: e.id,
          nombre: e.nombre || "Sin nombre",
          startTime: e.startTime,
          valor,
          costos: costosEv,
          utilidad: valor - costosEv,
        };
      })
      .sort((a, b) => t(a.startTime) - t(b.startTime));
  }, [mesAbierto, events, costos, tz]);

  // Drill-down: los depósitos que el venue se quedó ese mes — de quién eran,
  // de qué evento y cuánto. Es el desglose de la columna Depósitos aplicados.
  const detalleAplicados = useMemo(() => {
    if (!mesAbierto) return [];
    const { from, to } = getMonthRange(mesAbierto);
    const nombreEvento = {};
    (events || []).forEach((e) => { nombreEvento[e.id] = e.nombre || "Sin nombre"; });
    const t = (v) => (v?.toDate ? v.toDate() : new Date(v || 0));
    const out = [];
    (depositos || []).forEach((dep) => {
      asientosDeDeposito(dep).forEach((a) => {
        if (a.tipo !== "deposito_aplicado" || !isInRange(a.fecha, from, to, tz)) return;
        out.push({
          id: a.id,
          fecha: a.fecha,
          concepto: a.concepto,
          fuente: a.fuente,
          proveedorId: a.proveedorId,
          proveedorNombre: a.proveedorNombre,
          eventId: a.eventId,
          eventNombre: a.eventId ? nombreEvento[a.eventId] || null : null,
          monto: a.monto,
          parcial: !!a.parcial,
          montoTotal: Number(dep.monto) || 0,
        });
      });
    });
    return out.sort((a, b) => t(a.fecha) - t(b.fecha));
  }, [mesAbierto, depositos, events, tz]);

  // Drill-down: las cancelaciones del mes, con lo retenido y lo gastado.
  const detalleCancelaciones = useMemo(() => {
    if (!mesAbierto) return [];
    const { from, to } = getMonthRange(mesAbierto);
    return cancelacionesRetenidas({ events, costos, refunds, desde: from, hasta: to, timeZone: tz }).lista;
  }, [mesAbierto, events, costos, refunds, tz]);

  const conDatos = serie.some((m) => m.eventos > 0) || hayResultado;
  const celda = (v, cls = "text-zinc-600 dark:text-zinc-300") =>
    v !== 0 ? <span className={cls}>{formatMXN(v)}</span> : <span className="text-zinc-300 dark:text-zinc-600">—</span>;

  return (
    <div className="space-y-6">
      <SelectorAnio anio={anio} years={years} setAnio={setAnio} />

      {!conDatos ? (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-10 text-center text-sm text-zinc-400 shadow-sm">
          Sin eventos en {anio}.
        </div>
      ) : (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-zinc-50 dark:bg-zinc-800/40">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Mes</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Eventos</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Valor</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Costos de sus eventos</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">
                    {hayResultado ? "Utilidad de eventos" : "Utilidad"}
                  </th>
                  {hayAplicados && (
                    <th
                      className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500"
                      title="Depósitos en garantía que el venue se quedó ese mes. Son ingreso del venue, no de un evento."
                    >
                      Depósitos aplicados
                    </th>
                  )}
                  {hayCancelaciones && (
                    <th
                      className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500"
                      title="Lo que el venue retuvo de eventos cancelados ese mes, menos lo que esos eventos gastaron."
                    >
                      Cancelaciones
                    </th>
                  )}
                  {hayResultado && (
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Resultado</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                {serie.map((m) => {
                  const abierto = mesAbierto === m.key;
                  return (
                    <React.Fragment key={m.key}>
                      <tr
                        onClick={() => setMesAbierto(abierto ? null : m.key)}
                        className="cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition"
                        title="Ver los eventos del mes"
                      >
                        <td className="px-4 py-2 text-zinc-900 dark:text-white font-medium whitespace-nowrap">
                          <span className="inline-flex items-center gap-1">
                            <ChevronRightIcon
                              className={`h-4 w-4 text-zinc-400 transition-transform ${abierto ? "rotate-90" : ""}`}
                            />
                            {m.label}
                            {m.futuro && (
                              <span className="ml-1 text-[10px] uppercase tracking-wide text-zinc-400">
                                por venir
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right text-zinc-500">
                          {m.eventos > 0 ? m.eventos : <span className="text-zinc-300 dark:text-zinc-600">—</span>}
                        </td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          {m.futuro ? (
                            m.valor !== 0 ? (
                              <span className="text-zinc-400" title="Valor contratado, aún no devengado">
                                {formatMXN(m.valor)}
                              </span>
                            ) : (
                              <span className="text-zinc-300 dark:text-zinc-600">—</span>
                            )
                          ) : (
                            celda(m.valor)
                          )}
                        </td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          {m.futuro ? (
                            <span className="text-zinc-300 dark:text-zinc-600">—</span>
                          ) : (
                            celda(m.costosEventos, "text-red-600")
                          )}
                        </td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          {m.futuro ? (
                            <span className="text-zinc-300 dark:text-zinc-600">—</span>
                          ) : m.valor !== 0 || m.utilidad !== 0 ? (
                            celda(m.utilidad, m.utilidad < 0 ? "text-red-600 font-medium" : "text-emerald-600 font-medium")
                          ) : (
                            <span className="text-zinc-300 dark:text-zinc-600">—</span>
                          )}
                        </td>
                        {hayAplicados && (
                          <td className="px-4 py-2 text-right whitespace-nowrap">
                            {m.futuro ? (
                              <span className="text-zinc-300 dark:text-zinc-600">—</span>
                            ) : (
                              celda(m.depositosAplicados || 0, "text-amber-600")
                            )}
                          </td>
                        )}
                        {hayCancelaciones && (
                          <td className="px-4 py-2 text-right whitespace-nowrap">
                            {m.futuro ? (
                              <span className="text-zinc-300 dark:text-zinc-600">—</span>
                            ) : (
                              celda(m.cancelaciones || 0, (m.cancelaciones || 0) < 0 ? "text-red-600" : "text-amber-600")
                            )}
                          </td>
                        )}
                        {hayResultado && (
                          <td className="px-4 py-2 text-right whitespace-nowrap">
                            {m.futuro ? (
                              <span className="text-zinc-300 dark:text-zinc-600">—</span>
                            ) : (
                              (() => {
                                const resultado = m.utilidad + extras(m);
                                return m.valor !== 0 || resultado !== 0
                                  ? celda(resultado, resultado < 0 ? "text-red-600 font-semibold" : "text-emerald-600 font-semibold")
                                  : <span className="text-zinc-300 dark:text-zinc-600">—</span>;
                              })()
                            )}
                          </td>
                        )}
                      </tr>
                      {abierto && (detalleEventos || detalleAplicados.length > 0 || detalleCancelaciones.length > 0) && (
                        <tr>
                          <td colSpan={columnas} className="px-5 py-4 bg-zinc-50/70 dark:bg-zinc-800/30">
                            {!detalleEventos || detalleEventos.length === 0 ? (
                              <p className="text-xs text-zinc-400">Sin eventos este mes.</p>
                            ) : (
                              <div className="space-y-1">
                                <div className="flex items-baseline gap-2 text-[10px] uppercase tracking-wide text-zinc-400">
                                  <span className="w-24 shrink-0">Fecha</span>
                                  <span>Evento</span>
                                  <span className="ml-auto w-24 text-right">
                                    {m.futuro ? "Contratado" : "Ingresos"}
                                  </span>
                                  <span className="w-24 text-right">Costos</span>
                                  <span className="w-24 text-right">Utilidad</span>
                                </div>
                                {detalleEventos.map((e) => (
                                  <div key={e.id} className="flex items-baseline gap-2 text-xs">
                                    <span className="text-zinc-400 w-24 shrink-0">
                                      {formatCalendarDate(e.startTime, tz, { style: "short", weekday: "short", instante: true })}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => navigate(`/dashboard/eventos/${e.id}`)}
                                      className="truncate hover:underline text-left"
                                      style={{ color: ACCENT }}
                                    >
                                      {e.nombre}
                                    </button>
                                    <span className="ml-auto w-24 text-right text-zinc-600 dark:text-zinc-300 whitespace-nowrap">
                                      {formatMXN(e.valor)}
                                    </span>
                                    {m.futuro ? (
                                      <>
                                        <span className="w-24 text-right text-zinc-300 dark:text-zinc-600">—</span>
                                        <span className="w-24 text-right text-zinc-300 dark:text-zinc-600">—</span>
                                      </>
                                    ) : (
                                      <>
                                        <span className="w-24 text-right whitespace-nowrap">
                                          {e.costos !== 0 ? (
                                            <span className="text-red-600">{formatMXN(e.costos)}</span>
                                          ) : (
                                            <span className="text-zinc-300 dark:text-zinc-600">—</span>
                                          )}
                                        </span>
                                        <span
                                          className={`w-24 text-right font-medium whitespace-nowrap ${
                                            e.utilidad < 0 ? "text-red-600" : "text-emerald-600"
                                          }`}
                                        >
                                          {formatMXN(e.utilidad)}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                ))}
                                <div className="flex items-baseline gap-2 text-xs pt-1 border-t border-zinc-200/60 dark:border-zinc-700/40 font-semibold">
                                  <span className="w-24 shrink-0" />
                                  <span className="text-zinc-500">
                                    Total · {m.eventos} {m.eventos === 1 ? "evento" : "eventos"}
                                  </span>
                                  <span className="ml-auto w-24 text-right text-zinc-900 dark:text-white whitespace-nowrap">
                                    {formatMXN(m.valor)}
                                  </span>
                                  {m.futuro ? (
                                    <>
                                      <span className="w-24 text-right text-zinc-300 dark:text-zinc-600">—</span>
                                      <span className="w-24 text-right text-zinc-300 dark:text-zinc-600">—</span>
                                    </>
                                  ) : (
                                    <>
                                      <span className="w-24 text-right text-red-600 whitespace-nowrap">
                                        {m.costosEventos !== 0 ? formatMXN(m.costosEventos) : "—"}
                                      </span>
                                      <span
                                        className={`w-24 text-right whitespace-nowrap ${
                                          m.utilidad < 0 ? "text-red-600" : "text-emerald-600"
                                        }`}
                                      >
                                        {formatMXN(m.utilidad)}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Depósitos aplicados del mes: ingreso del venue que no
                                es de ningún evento, y el Resultado que los suma. */}
                            {!m.futuro && detalleAplicados.length > 0 && (
                              <div className="mt-4 pt-3 border-t border-zinc-200/60 dark:border-zinc-700/40 space-y-1">
                                <div className="flex items-baseline gap-2 text-[10px] uppercase tracking-wide text-zinc-400">
                                  <span className="w-24 shrink-0">Fecha</span>
                                  <span>Depósito aplicado</span>
                                  <span className="ml-auto w-24 text-right">Ingreso</span>
                                </div>
                                {detalleAplicados.map((dep) => (
                                  <div key={dep.id} className="flex items-baseline gap-2 text-xs">
                                    <span className="text-zinc-400 w-24 shrink-0">
                                      {formatCalendarDate(dep.fecha, tz, { style: "short", weekday: "short" })}
                                    </span>
                                    <span className="min-w-0 truncate text-zinc-700 dark:text-zinc-300">
                                      {/* De quién era el depósito, siempre por nombre: el
                                          concepto puede ser la descripción libre del doc y
                                          no decirlo. */}
                                      {dep.fuente === "proveedor" ? (
                                        <>
                                          <span className="text-zinc-400">Depósito de </span>
                                          <button
                                            type="button"
                                            onClick={() => dep.proveedorId && navigate(`/dashboard/proveedores/${dep.proveedorId}`)}
                                            className="font-medium hover:underline"
                                            style={{ color: ACCENT }}
                                          >
                                            {dep.proveedorNombre || "proveedor sin nombre"}
                                          </button>
                                          {dep.concepto &&
                                            dep.concepto !== `Depósito de ${dep.proveedorNombre || "proveedor"}` && (
                                              <span className="text-zinc-400"> · {dep.concepto}</span>
                                            )}
                                        </>
                                      ) : (
                                        <>
                                          <span className="text-zinc-400">Depósito del cliente</span>
                                          {dep.concepto && dep.concepto !== "Depósito en garantía" && (
                                            <span className="text-zinc-400"> · {dep.concepto}</span>
                                          )}
                                        </>
                                      )}
                                      {dep.eventId && (
                                        <>
                                          <span className="text-zinc-400"> · </span>
                                          <button
                                            type="button"
                                            onClick={() => navigate(`/dashboard/eventos/${dep.eventId}`)}
                                            className="hover:underline"
                                            style={{ color: ACCENT }}
                                          >
                                            {dep.eventNombre || "Evento"}
                                          </button>
                                        </>
                                      )}
                                      {dep.parcial && (
                                        <span className="text-amber-600">
                                          {" · "}parcial de {formatMXN(dep.montoTotal)}
                                        </span>
                                      )}
                                    </span>
                                    <span className="ml-auto w-24 text-right font-medium text-amber-600 whitespace-nowrap">
                                      {formatMXN(dep.monto)}
                                    </span>
                                  </div>
                                ))}
                                <div className="flex items-baseline gap-2 text-xs pt-1 border-t border-zinc-200/60 dark:border-zinc-700/40 font-semibold">
                                  <span className="w-24 shrink-0" />
                                  <span className="text-zinc-500">
                                    Depósitos aplicados · {detalleAplicados.length}
                                  </span>
                                  <span className="ml-auto w-24 text-right text-amber-600 whitespace-nowrap">
                                    {formatMXN(m.depositosAplicados || 0)}
                                  </span>
                                </div>
                              </div>
                            )}

                            {/* Cancelaciones del mes: lo que se retuvo de eventos
                                cancelados, menos lo que esos eventos gastaron. */}
                            {!m.futuro && detalleCancelaciones.length > 0 && (
                              <div className="mt-4 pt-3 border-t border-zinc-200/60 dark:border-zinc-700/40 space-y-1">
                                <div className="flex items-baseline gap-2 text-[10px] uppercase tracking-wide text-zinc-400">
                                  <span className="w-24 shrink-0">Cancelado</span>
                                  <span>Evento cancelado</span>
                                  <span className="ml-auto w-24 text-right">Retenido</span>
                                  <span className="w-24 text-right">Costos</span>
                                  <span className="w-24 text-right">Resultado</span>
                                </div>
                                {detalleCancelaciones.map((c) => (
                                  <div key={c.id} className="flex items-baseline gap-2 text-xs">
                                    <span className="text-zinc-400 w-24 shrink-0">
                                      {formatCalendarDate(c.fecha, tz, { style: "short", weekday: "short", instante: true })}
                                    </span>
                                    <span className="min-w-0 truncate">
                                      <button
                                        type="button"
                                        onClick={() => navigate(`/dashboard/eventos/${c.id}`)}
                                        className="hover:underline text-left"
                                        style={{ color: ACCENT }}
                                      >
                                        {c.nombre}
                                      </button>
                                      {c.reembolsado > 0.01 && (
                                        <span className="text-zinc-400"> · reembolsado {formatMXN(c.reembolsado)}</span>
                                      )}
                                    </span>
                                    <span className="ml-auto w-24 text-right text-zinc-600 dark:text-zinc-300 whitespace-nowrap">
                                      {formatMXN(c.retenido)}
                                    </span>
                                    <span className="w-24 text-right whitespace-nowrap">
                                      {c.costos !== 0 ? (
                                        <span className="text-red-600">{formatMXN(c.costos)}</span>
                                      ) : (
                                        <span className="text-zinc-300 dark:text-zinc-600">—</span>
                                      )}
                                    </span>
                                    <span
                                      className={`w-24 text-right font-medium whitespace-nowrap ${
                                        c.resultado < 0 ? "text-red-600" : "text-amber-600"
                                      }`}
                                    >
                                      {formatMXN(c.resultado)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {!m.futuro && (detalleAplicados.length > 0 || detalleCancelaciones.length > 0) && (
                              <div className="mt-3 flex items-baseline gap-2 text-xs pt-1 border-t border-zinc-200/60 dark:border-zinc-700/40 font-semibold">
                                <span className="w-24 shrink-0" />
                                <span className="text-zinc-900 dark:text-white">
                                  Resultado del mes · utilidad de eventos
                                  {detalleAplicados.length > 0 && " + depósitos aplicados"}
                                  {detalleCancelaciones.length > 0 && " + cancelaciones"}
                                </span>
                                <span
                                  className={`ml-auto w-24 text-right whitespace-nowrap ${
                                    m.utilidad + extras(m) < 0 ? "text-red-600" : "text-emerald-600"
                                  }`}
                                >
                                  {formatMXN(m.utilidad + extras(m))}
                                </span>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot className="bg-zinc-50 dark:bg-zinc-800/40 border-t border-zinc-200 dark:border-zinc-700/60">
                <tr>
                  <td className="px-4 py-2.5 font-semibold text-zinc-900 dark:text-white">Transcurrido {anio}</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-zinc-900 dark:text-white">{transcurrido.eventos}</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-zinc-900 dark:text-white whitespace-nowrap">{formatMXN(transcurrido.valor)}</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-red-600 whitespace-nowrap">{formatMXN(transcurrido.costosEventos)}</td>
                  <td className={`px-4 py-2.5 text-right font-semibold whitespace-nowrap ${transcurrido.utilidad < 0 ? "text-red-600" : "text-emerald-600"}`}>
                    {formatMXN(transcurrido.utilidad)}
                  </td>
                  {hayAplicados && (
                    <td className="px-4 py-2.5 text-right font-semibold text-amber-600 whitespace-nowrap">
                      {formatMXN(transcurrido.depositosAplicados)}
                    </td>
                  )}
                  {hayCancelaciones && (
                    <td className={`px-4 py-2.5 text-right font-semibold whitespace-nowrap ${
                      transcurrido.cancelaciones < 0 ? "text-red-600" : "text-amber-600"
                    }`}>
                      {formatMXN(transcurrido.cancelaciones)}
                    </td>
                  )}
                  {hayResultado && (
                    <td className={`px-4 py-2.5 text-right font-semibold whitespace-nowrap ${
                      transcurrido.utilidad + extras(transcurrido) < 0 ? "text-red-600" : "text-emerald-600"
                    }`}>
                      {formatMXN(transcurrido.utilidad + extras(transcurrido))}
                    </td>
                  )}
                </tr>
                {porVenir.eventos > 0 && (
                  <tr className="border-t border-zinc-200 dark:border-zinc-700/60">
                    <td className="px-4 py-2.5 font-medium text-zinc-500">Contratado por venir</td>
                    <td className="px-4 py-2.5 text-right font-medium text-zinc-500">{porVenir.eventos}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-zinc-500 whitespace-nowrap">{formatMXN(porVenir.valor)}</td>
                    <td className="px-4 py-2.5 text-right text-zinc-300 dark:text-zinc-600">—</td>
                    <td className="px-4 py-2.5 text-right text-zinc-300 dark:text-zinc-600">—</td>
                    {Array.from({ length: columnas - 5 }, (_, i) => (
                      <td key={i} className="px-4 py-2.5 text-right text-zinc-300 dark:text-zinc-600">—</td>
                    ))}
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <p className="text-xs text-zinc-400">
        El valor y la utilidad pertenecen al mes en que ocurre el evento, se
        cobre cuando se cobre; los costos cuentan al evento aunque se paguen
        en otro mes.
        {hayAplicados && (
          <>
            {" "}Los depósitos en garantía que el venue se quedó cuentan como
            ingreso el mes en que se aplicaron, aparte de la utilidad de los
            eventos.
          </>
        )}
        {hayCancelaciones && (
          <>
            {" "}Lo retenido de un evento cancelado (lo cobrado menos lo
            reembolsado y lo que ese evento gastó) cuenta el mes en que se
            canceló, aparte de la utilidad de los eventos.
          </>
        )}
        {hayResultado && " El Resultado suma todo."}
        {" "}El dinero que entra y sale cada mes vive en el reporte Flujo de
        caja.
      </p>
    </div>
  );
};

export default ReporteEstadoResultados;
